import { FunctionComponent, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Button from "../../generic/Button";
import { sendgrid, types } from "../../../utils";
import Map from "../../../assets/google-map.png";

const ContactFormSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("First name is required")
    .max(50, "First name must be less than 50 characters"),

  lastName: yup
    .string()
    .required("Last name is required")
    .max(50, "Last name must be less than 50 characters"),

  phone: yup
    .string()
    .required("Phone number is required")
    .matches(
      /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
      "Please enter a valid phone number"
    ),

  email: yup
    .string()
    .required("Email is required")
    .email("Please enter a valid email address"),

  message: yup
    .string()
    .required("Message is required")
    .min(10, "Message must be at least 10 characters")
    .max(500, "Message must be less than 500 characters"),
});

interface IContactForm {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  message: string;
}

const Contact: FunctionComponent = () => {
  const [submitting, setSubmitting] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IContactForm>({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      message: "",
    },
    resolver: yupResolver(ContactFormSchema),
  });

  const submit = async (values: IContactForm) => {
    setSubmitting(true);
    const { firstName, lastName, phone, email, message } = values;
    try {
      // TODO: Replace with actual API call
      console.log({ values });

      const result = await sendgrid.sendQuestion(
        firstName,
        lastName,
        phone,
        email,
        message
      );

      reset();
      console.log({ result });
      alert("Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="contact" className="px-8">
      <ContactHeader />
      <ContactInfoSection />
      <ContactFormSection
        register={register}
        handleSubmit={handleSubmit}
        submit={submit}
        errors={errors}
        submitting={submitting}
      />
    </div>
  );
};

const ContactHeader: FunctionComponent = () => (
  <h2 className="hidden lg:block text-4xl tracking-wider font-sans font-semibold mb-8">
    Contact Us
  </h2>
);

const ContactInfoSection: FunctionComponent = () => (
  <div className="w-full flex flex-col lg:flex-row lg:justify-between mb-6 text-center lg:text-left">
    <CompanyInfo />
    <PhoneSection />
    <EmailSection />
  </div>
);

const CompanyInfo: FunctionComponent = () => (
  <div>
    <h4 className="text-primary text-4xl lg:text-2xl font-semibold lg:font-medium mb-3">
      ClientVitals
    </h4>
    <p className="font-semibold lg:font-medium text-xl lg:text-md">
      Office Location/Mailing Address:
    </p>
    <p>145 Pine Haven Shores Road</p>
    <p>Suite 2173</p>
    <p>Shelburne, VT 05482</p>
  </div>
);

const PhoneSection: FunctionComponent = () => (
  <>
    <div className="hidden lg:block">
      <p className="font-medium">Phone:</p>
      <p>(802) 922-6268</p>
    </div>

    <div className="flex items-center justify-center lg:hidden my-4">
      <a href="tel:+18029226268">
        <Button
          text="Call +1 (802) 922-6268"
          theme={types.buttonTypes.BUTTON_THEME_TYPES.PRIMARY}
          styles="w-[350px]"
        />
      </a>
    </div>
  </>
);

const EmailSection: FunctionComponent = () => (
  <>
    <div className="hidden lg:block lg:mr-[20%]">
      <p className="font-medium">Email:</p>
      <p>info@clientvitals.com</p>
    </div>

    <a
      href="mailto:info@clientvitals.com"
      className="flex items-center justify-center lg:hidden"
    >
      <Button
        text="EMAIL"
        theme={types.buttonTypes.BUTTON_THEME_TYPES.PRIMARY}
        styles="w-[350px]"
      />
    </a>
  </>
);

interface ContactFormSectionProps {
  register: any;
  handleSubmit: any;
  submit: (values: IContactForm) => Promise<void>;
  errors: any;
  submitting: boolean;
}

const ContactFormSection: FunctionComponent<ContactFormSectionProps> = ({
  register,
  handleSubmit,
  submit,
  errors,
  submitting,
}) => (
  <div className="mb-6 flex justify-between flex-col lg:flex-row">
    <ContactForm
      register={register}
      handleSubmit={handleSubmit}
      submit={submit}
      errors={errors}
      submitting={submitting}
    />
    <MapSection />
  </div>
);

interface ContactFormProps {
  register: any;
  handleSubmit: any;
  submit: (values: IContactForm) => Promise<void>;
  errors: any;
  submitting: boolean;
}

const ContactForm: FunctionComponent<ContactFormProps> = ({
  register,
  handleSubmit,
  submit,
  errors,
  submitting,
}) => (
  <form className="bg-[#F5F5F5] lg:w-[45%] p-4">
    <TextInput
      register={register}
      id="firstName"
      placeholder="first name..."
      label="First Name"
      error={errors.firstName?.message}
    />

    <TextInput
      register={register}
      id="lastName"
      placeholder="last name..."
      label="Last Name"
      error={errors.lastName?.message}
    />

    <TextInput
      register={register}
      id="phone"
      placeholder="(555)555-5555"
      label="Phone"
      error={errors.phone?.message}
    />

    <TextInput
      register={register}
      id="email"
      placeholder="mail@mail.com"
      label="Email"
      error={errors.email?.message}
    />

    <MessageField register={register} error={errors.message?.message} />

    <Button
      text={submitting ? "Sending..." : "Send Message"}
      onClick={handleSubmit(submit)}
      styles="w-[250px] text-md rounded-none p-2"
      disabled={submitting}
      theme={types.buttonTypes.BUTTON_THEME_TYPES.PRIMARY}
    />
  </form>
);

interface TextInputProps {
  register: any;
  id: string;
  placeholder: string;
  label: string;
  error?: string;
}

const TextInput: FunctionComponent<TextInputProps> = ({
  register,
  id,
  placeholder,
  label,
  error,
}) => (
  <div className="mb-4">
    <label className="block text-gray-700 text-sm font-bold mb-2">
      {label}
    </label>
    <input
      {...register(id)}
      type={id === "email" ? "email" : id === "phone" ? "tel" : "text"}
      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:border-primary"
      placeholder={placeholder}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

interface MessageFieldProps {
  register: any;
  error?: string;
}

const MessageField: FunctionComponent<MessageFieldProps> = ({
  register,
  error,
}) => (
  <div className="mb-4">
    <label className="block text-gray-700 text-sm font-bold mb-2">
      Message
    </label>
    <textarea
      {...register("message")}
      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:border-primary"
      rows={6}
      cols={50}
      placeholder="Message..."
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const MapSection: FunctionComponent = () => (
  <div className="mx-auto mt-8 lg:mt-0 lg:w-[45%]">
    <img src={Map} height={600} width={620} alt="Map showing office location" />
  </div>
);

export default Contact;
